import { useEffect, useState } from 'react';
import { TicketLogin } from '@matix/ui';
import { createStorage } from '@matix/utils';
import { useCashierAuthStore } from '../../store/cashierAuthStore';
import { useTheme } from '../../store/theme';
import { useLang } from '../../lib/language';
import { MoonIcon, SunIcon } from '../../components/icons';
import { takeSignInLink } from '../../lib/demoLink';
import { BiometricUnlock } from './BiometricUnlock';

/**
 * Cashier login — the single sign-in view for the app.
 *
 * The till asks for TWO things: the company and the password. It has no
 * username field — a password is unique within a tenant, so it names the
 * cashier on its own, and a counter is the worst place to make someone type a
 * third thing. The server does the identifying (`findUserByPassword`) and
 * still returns the username, which is what the pickers and the receipt show.
 *
 * Presentation is the shared `TicketLogin` from @matix/ui, so the till, the
 * admin console, the KDS and the appointments calendar all sign in on the
 * same paper. What stays here is cashier-specific: the remembered tenant
 * slug, the per-field validation the till needs (an operator mistyping one
 * field shouldn't be told "check your details"), and the theme toggle that
 * rides along in the ticket's header.
 *
 * On submit the multi-tenant auth store takes over; App.tsx then routes to
 * the subsidiary → branch → drawer pickers. There is no second login screen.
 */

const REMEMBER_STORE = createStorage('pos:cashier');
const TENANT_SLUG_KEY = 'last_tenant_slug';

const STRINGS = {
  en: {
    errSlug: 'Company is required.',
    errPassword: 'Password is required.',
  },
  ar: {
    errSlug: 'اسم الشركة مطلوب.',
    errPassword: 'كلمة المرور مطلوبة.',
  },
} as const;

export function LoginScreen({ banner }: { banner: string | null }): JSX.Element {
  const login = useCashierAuthStore((s) => s.login);
  const error = useCashierAuthStore((s) => s.error);
  const clearError = useCashierAuthStore((s) => s.clearError);
  const isLoading = useCashierAuthStore((s) => s.state.kind === 'logging-in');

  const themeMode = useTheme((s) => s.theme);
  const toggleTheme = useTheme((s) => s.toggle);
  const { lang, setLang } = useLang();

  const [values, setValues] = useState({
    tenant: REMEMBER_STORE.get<string>(TENANT_SLUG_KEY) ?? '',
    // TicketLogin's value shape still carries a username; the field is hidden
    // here, so it stays empty and is never sent.
    username: '',
    password: '',
    remember: true,
  });
  const [fieldErrors, setFieldErrors] = useState<{
    tenant?: string;
    password?: string;
  }>({});

  const t = STRINGS[lang];

  // A sign-in link (?tenant=&pin=, e.g. matixerp.com «جرّب نقاط البيع») signs in
  // on arrival with the same call the form makes. Taken once, so a failed
  // attempt leaves the ordinary form, with the company filled in, to retry.
  useEffect(() => {
    const link = takeSignInLink();
    if (!link) return;
    setValues((v) => ({ ...v, tenant: link.tenant }));
    void login(link.tenant, link.password);
  }, [login]);

  async function onSubmit(): Promise<void> {
    if (isLoading) return;
    if (error) clearError();

    const fe: typeof fieldErrors = {};
    if (!values.tenant.trim()) fe.tenant = t.errSlug;
    if (!values.password) fe.password = t.errPassword;
    setFieldErrors(fe);
    if (Object.keys(fe).length > 0) return;

    if (values.remember) REMEMBER_STORE.set(TENANT_SLUG_KEY, values.tenant.trim());
    else REMEMBER_STORE.remove(TENANT_SLUG_KEY);

    // The store catches API errors and exposes a friendly `error` string —
    // it never throws here, so no try/catch is needed.
    await login(values.tenant.trim(), values.password);
  }

  return (
    <TicketLogin
      lang={lang}
      onLangChange={setLang}
      console={{
        en: 'Terminal · Powered by MatixERP',
        ar: 'نقطة البيع · مدعوم من MatixERP',
      }}
      title={{ en: 'Sign in to terminal', ar: 'تسجيل الدخول إلى الجهاز' }}
      tenantLabel={{ en: 'Company', ar: 'الشركة' }}
      rememberLabel={{
        en: 'Remember this company on this terminal',
        ar: 'تذكّر هذه الشركة على هذا الجهاز',
      }}
      showUsername={false}
      extra={<BiometricUnlock />}
      values={values}
      onChange={(patch) => {
        setValues((v) => ({ ...v, ...patch }));
        // Clear a field's error the moment the operator edits it, rather than
        // making them re-submit to find out they fixed it.
        const touched = Object.keys(patch) as Array<keyof typeof patch>;
        if (touched.some((k) => k in fieldErrors)) {
          setFieldErrors((fe) => {
            const next = { ...fe };
            for (const k of touched) delete next[k as keyof typeof next];
            return next;
          });
        }
      }}
      onSubmit={() => void onSubmit()}
      busy={isLoading}
      banner={banner}
      error={error}
      fieldErrors={fieldErrors}
      extraControls={
        <button
          type="button"
          onClick={toggleTheme}
          title="Toggle theme"
          aria-label="Toggle theme"
        >
          {themeMode === 'light' ? <MoonIcon size={16} /> : <SunIcon size={16} />}
        </button>
      }
    />
  );
}
